import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { useMasterData } from '../contexts/MasterDataContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Plus, Edit, Trash2, MapPin, Gamepad2, RefreshCw,
  ChevronDown, ChevronUp, Database, Upload, Image, Link, X,
  Ruler, IndianRupee, Info, TrendingUp
} from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

const ALL_AVAILABLE_GAMES = ['Pickleball', 'Badminton', 'Cricket', 'Football Half', 'Football Full'];

// ── Format helpers ────────────────────────────────────────────────────────────
const fmtArea = (v) =>
  v != null && v !== '' ? `${Number(v).toLocaleString('en-IN')} sq. ft.` : null;

const fmtCapital = (v) =>
  v != null && v !== '' ? `₹${Number(v).toLocaleString('en-IN')}` : null;

const hasFacilityData = (venue) =>
  venue.gross_facility_area != null ||
  venue.usable_sports_area != null ||
  venue.revenue_generating_area != null ||
  venue.invested_capital != null;

// ── Empty form factories ──────────────────────────────────────────────────────
const emptyVenueForm = () => ({
  name: '',
  location: '',
  full_address: '',
  google_maps_link: '',
  latitude: '',
  longitude: '',
  images: [],
  is_active: true,
  gross_facility_area: '',
  usable_sports_area: '',
  revenue_generating_area: '',
  invested_capital: '',
  investment_remarks: '',
});

export default function VenueCourtMasterPage() {
  const { admin } = useAuth();
  const { refreshData } = useMasterData();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVenues, setExpandedVenues] = useState([]);
  const [expandedFacility, setExpandedFacility] = useState([]); // per-venue facility details panel

  // Modal states
  const [venueModalOpen, setVenueModalOpen] = useState(false);
  const [courtModalOpen, setCourtModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [migrating, setMigrating] = useState(false);

  // Form data
  const [editingVenue, setEditingVenue] = useState(null);
  const [editingCourt, setEditingCourt] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [selectedVenueForCourt, setSelectedVenueForCourt] = useState(null);
  const [areaOverride, setAreaOverride] = useState(false);
  const [areaWarning, setAreaWarning] = useState('');

  const [venueForm, setVenueForm] = useState(emptyVenueForm());
  const [courtForm, setCourtForm] = useState({ name: '', games: [], price_per_slot: 0, is_active: true });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageInputMode, setImageInputMode] = useState('upload');
  const fileInputRef = useRef(null);

  const isAdmin = admin?.role === 'admin' || admin?.email === 'admin@maks.com';

  useEffect(() => { fetchVenues(); }, []);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/masters/venues-with-courts?include_inactive=true`);
      setVenues(response.data.venues || []);
      setExpandedVenues(response.data.venues?.map(v => v.id) || []);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
      toast.error('Failed to load venue data');
    } finally {
      setLoading(false);
    }
  };

  const toggleVenueExpand = (venueId) => {
    setExpandedVenues(prev =>
      prev.includes(venueId) ? prev.filter(id => id !== venueId) : [...prev, venueId]
    );
  };

  const toggleFacilityExpand = (e, venueId) => {
    e.stopPropagation();
    setExpandedFacility(prev =>
      prev.includes(venueId) ? prev.filter(id => id !== venueId) : [...prev, venueId]
    );
  };

  // ── Venue CRUD ────────────────────────────────────────────────────────────
  const handleCreateVenue = () => {
    setEditingVenue(null);
    setVenueForm(emptyVenueForm());
    setAreaOverride(false);
    setAreaWarning('');
    setNewImageUrl('');
    setVenueModalOpen(true);
  };

  const handleEditVenue = (venue) => {
    setEditingVenue(venue);
    setVenueForm({
      name: venue.name,
      location: venue.location || '',
      full_address: venue.full_address || venue.address || '',
      google_maps_link: venue.google_maps_link || '',
      latitude: venue.latitude || '',
      longitude: venue.longitude || '',
      images: venue.images || [],
      is_active: venue.is_active,
      gross_facility_area: venue.gross_facility_area != null ? String(venue.gross_facility_area) : '',
      usable_sports_area: venue.usable_sports_area != null ? String(venue.usable_sports_area) : '',
      revenue_generating_area: venue.revenue_generating_area != null ? String(venue.revenue_generating_area) : '',
      invested_capital: venue.invested_capital != null ? String(venue.invested_capital) : '',
      investment_remarks: venue.investment_remarks || '',
    });
    setAreaOverride(false);
    setAreaWarning('');
    setNewImageUrl('');
    setVenueModalOpen(true);
  };

  const addImageToVenue = () => {
    if (newImageUrl.trim()) {
      setVenueForm(prev => ({ ...prev, images: [...(prev.images || []), newImageUrl.trim()] }));
      setNewImageUrl('');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP images.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/files/upload?category=venues`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = `${API}/files/${response.data.id}`;
      setVenueForm(prev => ({ ...prev, images: [...(prev.images || []), imageUrl] }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImageFromVenue = (index) => {
    setVenueForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSaveVenue = async () => {
    if (!venueForm.name.trim()) {
      toast.error('Venue name is required');
      return;
    }

    // ── Area validation ──────────────────────────────────────────────────────
    const gfa = venueForm.gross_facility_area !== '' ? parseFloat(venueForm.gross_facility_area) : null;
    const usa = venueForm.usable_sports_area !== '' ? parseFloat(venueForm.usable_sports_area) : null;
    const rga = venueForm.revenue_generating_area !== '' ? parseFloat(venueForm.revenue_generating_area) : null;

    if (gfa !== null && gfa < 0) { toast.error('Gross Facility Area must be ≥ 0'); return; }
    if (usa !== null && usa < 0) { toast.error('Usable Sports Area must be ≥ 0'); return; }
    if (rga !== null && rga < 0) { toast.error('Revenue-Generating Area must be ≥ 0'); return; }

    let warning = '';
    if (gfa !== null && usa !== null && usa > gfa) {
      warning = `Usable Sports Area (${usa.toLocaleString('en-IN')} sq. ft.) exceeds Gross Facility Area (${gfa.toLocaleString('en-IN')} sq. ft.).`;
    }
    if (gfa !== null && rga !== null && rga > gfa) {
      warning = warning
        ? warning + ` Revenue-Generating Area (${rga.toLocaleString('en-IN')} sq. ft.) also exceeds Gross Facility Area.`
        : `Revenue-Generating Area (${rga.toLocaleString('en-IN')} sq. ft.) exceeds Gross Facility Area (${gfa.toLocaleString('en-IN')} sq. ft.).`;
    }
    if (warning && !areaOverride) {
      setAreaWarning(warning);
      return;
    }

    try {
      const cap = venueForm.invested_capital !== '' ? parseFloat(venueForm.invested_capital) : null;
      if (cap !== null && cap < 0) { toast.error('Invested Capital must be ≥ 0'); return; }

      const payload = {
        name: venueForm.name,
        location: venueForm.location,
        full_address: venueForm.full_address,
        google_maps_link: venueForm.google_maps_link,
        latitude: venueForm.latitude ? parseFloat(venueForm.latitude) : null,
        longitude: venueForm.longitude ? parseFloat(venueForm.longitude) : null,
        images: venueForm.images,
        is_active: venueForm.is_active,
        gross_facility_area: gfa,
        usable_sports_area: usa,
        revenue_generating_area: rga,
        invested_capital: cap,
        investment_remarks: venueForm.investment_remarks,
      };

      if (editingVenue) {
        await axios.put(`${API}/masters/venues/${editingVenue.id}`, payload);
        toast.success('Venue updated successfully');
      } else {
        await axios.post(`${API}/masters/venues`, payload);
        toast.success('Venue created successfully');
      }
      setVenueModalOpen(false);
      setAreaWarning('');
      setAreaOverride(false);
      fetchVenues();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save venue');
    }
  };

  // ── Court CRUD ────────────────────────────────────────────────────────────
  const handleCreateCourt = (venue) => {
    setEditingCourt(null);
    setSelectedVenueForCourt(venue);
    setCourtForm({ name: '', games: [], price_per_slot: 0, is_active: true });
    setCourtModalOpen(true);
  };

  const handleEditCourt = (court, venue) => {
    setEditingCourt(court);
    setSelectedVenueForCourt(venue);
    setCourtForm({
      name: court.name,
      games: court.games || [],
      price_per_slot: court.price_per_slot || 0,
      is_active: court.is_active
    });
    setCourtModalOpen(true);
  };

  const handleSaveCourt = async () => {
    if (!courtForm.name.trim()) { toast.error('Court name is required'); return; }
    if (courtForm.games.length === 0) { toast.error('Select at least one game for this court'); return; }
    try {
      if (editingCourt) {
        await axios.put(`${API}/masters/courts/${editingCourt.id}`, courtForm);
        toast.success('Court updated successfully');
      } else {
        await axios.post(`${API}/masters/courts`, { ...courtForm, venue_id: selectedVenueForCourt.id });
        toast.success('Court created successfully');
      }
      setCourtModalOpen(false);
      fetchVenues();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save court');
    }
  };

  const handleDeleteClick = (item, type) => {
    setDeletingItem({ ...item, type });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deletingItem.type === 'venue') {
        await axios.delete(`${API}/masters/venues/${deletingItem.id}`);
        toast.success('Venue deleted successfully');
      } else {
        await axios.delete(`${API}/masters/courts/${deletingItem.id}`);
        toast.success('Court deleted successfully');
      }
      setDeleteDialogOpen(false);
      setDeletingItem(null);
      fetchVenues();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleGameForCourt = (game) => {
    setCourtForm(prev => ({
      ...prev,
      games: prev.games.includes(game) ? prev.games.filter(g => g !== game) : [...prev.games, game]
    }));
  };

  const handleSeedFromHardcoded = async () => {
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/masters/seed-from-hardcoded`);
      toast.success(`Migration complete! Created ${response.data.created_venues} venues, ${response.data.created_courts} courts`);
      fetchVenues();
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Access denied. Admin only.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="venue-court-master-page">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
            Venue & Court Master
          </h1>
          <p className="text-slate-600 mt-1">Manage venues and courts across the platform</p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-3">
          <Button onClick={handleCreateVenue} data-testid="add-venue-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Venue
          </Button>
          <Button variant="outline" onClick={() => fetchVenues()}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          {venues.length === 0 && (
            <Button variant="secondary" onClick={handleSeedFromHardcoded} disabled={migrating} data-testid="seed-data-btn">
              <Database className="w-4 h-4 mr-2" />
              {migrating ? 'Migrating...' : 'Seed From Existing Data'}
            </Button>
          )}
        </div>

        {/* Venues List */}
        {venues.length === 0 ? (
          <Card className="p-12 text-center border-slate-200">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p className="text-slate-500 mb-4">No venues configured yet</p>
            <Button onClick={handleSeedFromHardcoded} disabled={migrating}>
              <Database className="w-4 h-4 mr-2" />
              {migrating ? 'Migrating...' : 'Migrate Existing Data'}
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {venues.map((venue) => (
              <Card key={venue.id} className="overflow-hidden border-slate-200">
                {/* ── Venue Header ─────────────────────────────────────────── */}
                <div
                  className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => toggleVenueExpand(venue.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${venue.is_active ? 'bg-primary text-white' : 'bg-slate-400 text-white'}`}>
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-slate-900 flex items-center gap-2">
                          {venue.name}
                          {!venue.is_active && (
                            <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full">Inactive</span>
                          )}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          {(venue.location || venue.full_address || venue.address) && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {venue.location || venue.full_address || venue.address}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Gamepad2 className="w-3 h-3" />
                            {venue.courts?.length || 0} courts • {venue.games?.length || 0} games
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost" size="sm"
                        onClick={(e) => { e.stopPropagation(); handleEditVenue(venue); }}
                        data-testid={`edit-venue-${venue.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(venue, 'venue'); }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-venue-${venue.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {expandedVenues.includes(venue.id) ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Facility & Investment Details Panel ──────────────────── */}
                {hasFacilityData(venue) && (
                  <div className="border-b border-slate-100">
                    {/* Toggle strip */}
                    <button
                      className="w-full flex items-center justify-between px-4 py-2 bg-indigo-50 hover:bg-indigo-100 transition-colors text-left"
                      onClick={(e) => toggleFacilityExpand(e, venue.id)}
                      data-testid={`facility-toggle-${venue.id}`}
                    >
                      <div className="flex items-center gap-2 text-xs font-medium text-indigo-700">
                        <Ruler className="w-3.5 h-3.5" />
                        <span>Facility &amp; Investment Details</span>
                        {venue.invested_capital != null && (
                          <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs">
                            {fmtCapital(venue.invested_capital)} invested
                          </span>
                        )}
                      </div>
                      {expandedFacility.includes(venue.id) ? (
                        <ChevronUp className="w-4 h-4 text-indigo-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-indigo-500" />
                      )}
                    </button>

                    {/* Expanded content */}
                    {expandedFacility.includes(venue.id) && (
                      <div
                        className="px-4 py-4 bg-indigo-50/40 grid grid-cols-2 md:grid-cols-4 gap-4"
                        data-testid={`facility-details-${venue.id}`}
                      >
                        <FacilityMetric
                          icon={<Ruler className="w-4 h-4 text-blue-500" />}
                          label="Gross Facility Area"
                          value={fmtArea(venue.gross_facility_area)}
                        />
                        <FacilityMetric
                          icon={<Ruler className="w-4 h-4 text-emerald-500" />}
                          label="Usable Sports Area"
                          value={fmtArea(venue.usable_sports_area)}
                        />
                        <FacilityMetric
                          icon={<TrendingUp className="w-4 h-4 text-violet-500" />}
                          label="Revenue-Generating Area"
                          value={fmtArea(venue.revenue_generating_area)}
                        />
                        <FacilityMetric
                          icon={<IndianRupee className="w-4 h-4 text-amber-500" />}
                          label="Invested Capital"
                          value={fmtCapital(venue.invested_capital)}
                          highlight
                        />
                        {venue.investment_remarks && (
                          <div className="col-span-2 md:col-span-4 text-xs text-slate-500 italic pt-1 border-t border-indigo-100">
                            <span className="font-medium text-slate-600">Remarks: </span>
                            {venue.investment_remarks}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Courts List (Expandable) ──────────────────────────── */}
                {expandedVenues.includes(venue.id) && (
                  <div className="p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-slate-700">Courts</h4>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleCreateCourt(venue)}
                        data-testid={`add-court-${venue.id}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Court
                      </Button>
                    </div>

                    {(!venue.courts || venue.courts.length === 0) ? (
                      <p className="text-sm text-slate-400 italic py-4 text-center">No courts added yet</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {venue.courts.map((court) => (
                          <div
                            key={court.id}
                            className={`p-3 rounded-lg border ${court.is_active ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{court.name}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {court.games?.map((game) => (
                                    <span key={game} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                                      {game}
                                    </span>
                                  ))}
                                </div>
                                {!court.is_active && (
                                  <span className="text-xs text-slate-500 mt-1 block">Inactive</span>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleEditCourt(court, venue)}
                                  data-testid={`edit-court-${court.id}`}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteClick(court, 'court')}
                                  data-testid={`delete-court-${court.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ── Venue Modal ──────────────────────────────────────────────────────── */}
      <Dialog open={venueModalOpen} onOpenChange={(open) => {
        setVenueModalOpen(open);
        if (!open) { setAreaWarning(''); setAreaOverride(false); }
      }}>
        <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVenue ? 'Edit Venue' : 'Add New Venue'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="venue_name">Venue Name *</Label>
                <Input
                  id="venue_name"
                  value={venueForm.name}
                  onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })}
                  placeholder="e.g., MAKS Pickleball - ISF"
                  data-testid="venue-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue_location">Location (Short)</Label>
                <Input
                  id="venue_location"
                  value={venueForm.location}
                  onChange={(e) => setVenueForm({ ...venueForm, location: e.target.value })}
                  placeholder="e.g., Mira Road"
                  data-testid="venue-location-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue_full_address">Full Address</Label>
              <textarea
                id="venue_full_address"
                value={venueForm.full_address}
                onChange={(e) => setVenueForm({ ...venueForm, full_address: e.target.value })}
                placeholder="Full street address with landmark, city, pincode"
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="venue-full-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue_maps_link">Google Maps Link</Label>
              <Input
                id="venue_maps_link"
                value={venueForm.google_maps_link}
                onChange={(e) => setVenueForm({ ...venueForm, google_maps_link: e.target.value })}
                placeholder="https://maps.google.com/..."
                data-testid="venue-maps-link-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="venue_latitude">Latitude</Label>
                <Input
                  id="venue_latitude"
                  type="number" step="any"
                  value={venueForm.latitude}
                  onChange={(e) => setVenueForm({ ...venueForm, latitude: e.target.value })}
                  placeholder="e.g., 19.2832"
                  data-testid="venue-latitude-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue_longitude">Longitude</Label>
                <Input
                  id="venue_longitude"
                  type="number" step="any"
                  value={venueForm.longitude}
                  onChange={(e) => setVenueForm({ ...venueForm, longitude: e.target.value })}
                  placeholder="e.g., 72.8543"
                  data-testid="venue-longitude-input"
                />
              </div>
            </div>

            {/* Images section */}
            <div className="space-y-2">
              <Label>Venue Images</Label>
              <div className="flex gap-2 mb-2">
                <Button type="button" size="sm" variant={imageInputMode === 'upload' ? 'default' : 'outline'} onClick={() => setImageInputMode('upload')}>
                  <Upload className="w-4 h-4 mr-1" /> Upload File
                </Button>
                <Button type="button" size="sm" variant={imageInputMode === 'url' ? 'default' : 'outline'} onClick={() => setImageInputMode('url')}>
                  <Link className="w-4 h-4 mr-1" /> Enter URL
                </Button>
              </div>
              {imageInputMode === 'upload' && (
                <div className="flex gap-2">
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileUpload} className="hidden" id="venue-image-upload" />
                  <Button type="button" variant="outline" className="flex-1" disabled={uploadingImage} onClick={() => fileInputRef.current?.click()}>
                    {uploadingImage ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Image className="w-4 h-4 mr-2" />Choose Image (Max 5MB)</>}
                  </Button>
                </div>
              )}
              {imageInputMode === 'url' && (
                <div className="flex gap-2">
                  <Input value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)} placeholder="Enter image URL" className="flex-1" />
                  <Button type="button" variant="outline" onClick={addImageToVenue}><Plus className="w-4 h-4 mr-1" /> Add</Button>
                </div>
              )}
              {venueForm.images?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {venueForm.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={img} alt={`Venue ${idx + 1}`} className="w-full h-24 object-cover rounded-lg border shadow-sm" onError={(e) => e.target.src = 'https://via.placeholder.com/150?text=Error'} />
                      <button type="button" onClick={() => removeImageFromVenue(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600">
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                        {img.includes('/api/files/') ? 'Uploaded' : 'External URL'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {venueForm.images?.length === 0 && <p className="text-xs text-slate-500 mt-1">No images added yet. Upload files or paste image URLs.</p>}
            </div>

            {/* ── Facility & Investment Details ─────────────────────────── */}
            <div className="pt-3 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Ruler className="w-4 h-4 text-indigo-500" />
                <h4 className="text-sm font-semibold text-slate-700">Facility &amp; Investment Details</h4>
                <span className="text-xs text-slate-400 font-normal">(optional)</span>
              </div>

              {/* Area fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="gross_area">Gross Facility Area</Label>
                  <div className="relative">
                    <Input
                      id="gross_area"
                      type="number" min="0" step="0.01"
                      value={venueForm.gross_facility_area}
                      onChange={(e) => {
                        setVenueForm({ ...venueForm, gross_facility_area: e.target.value });
                        setAreaWarning(''); setAreaOverride(false);
                      }}
                      placeholder="0.00"
                      className="pr-14"
                      data-testid="venue-gross-area-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">sq. ft.</span>
                  </div>
                  <p className="text-xs text-slate-400">Total facility area incl. parking, reception, washrooms</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="usable_area">Usable Sports Area</Label>
                  <div className="relative">
                    <Input
                      id="usable_area"
                      type="number" min="0" step="0.01"
                      value={venueForm.usable_sports_area}
                      onChange={(e) => {
                        setVenueForm({ ...venueForm, usable_sports_area: e.target.value });
                        setAreaWarning(''); setAreaOverride(false);
                      }}
                      placeholder="0.00"
                      className="pr-14"
                      data-testid="venue-usable-area-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">sq. ft.</span>
                  </div>
                  <p className="text-xs text-slate-400">Courts, turfs, pools, other playable sports areas</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="revenue_area">Revenue-Generating Area</Label>
                  <div className="relative">
                    <Input
                      id="revenue_area"
                      type="number" min="0" step="0.01"
                      value={venueForm.revenue_generating_area}
                      onChange={(e) => {
                        setVenueForm({ ...venueForm, revenue_generating_area: e.target.value });
                        setAreaWarning(''); setAreaOverride(false);
                      }}
                      placeholder="0.00"
                      className="pr-14"
                      data-testid="venue-revenue-area-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">sq. ft.</span>
                  </div>
                  <p className="text-xs text-slate-400">Bookable courts, turfs, paid sports facilities</p>
                </div>
              </div>

              {/* Area validation warning + override */}
              {areaWarning && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-amber-800 font-medium">Area validation warning</p>
                      <p className="text-xs text-amber-700 mt-0.5">{areaWarning}</p>
                      <label className="flex items-center gap-2 mt-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={areaOverride}
                          onChange={(e) => setAreaOverride(e.target.checked)}
                          className="rounded border-amber-400"
                          data-testid="area-override-checkbox"
                        />
                        <span className="text-xs text-amber-800 font-medium">
                          I acknowledge this override and confirm it is correct (recorded in audit trail)
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Invested capital */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="invested_capital">Invested Capital</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">₹</span>
                    <Input
                      id="invested_capital"
                      type="number" min="0" step="1"
                      value={venueForm.invested_capital}
                      onChange={(e) => setVenueForm({ ...venueForm, invested_capital: e.target.value })}
                      placeholder="0"
                      className="pl-7"
                      data-testid="venue-invested-capital-input"
                    />
                  </div>
                  <p className="text-xs text-slate-400">Total capital invested in this venue (₹)</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="investment_remarks">Investment Remarks</Label>
                  <Input
                    id="investment_remarks"
                    value={venueForm.investment_remarks}
                    onChange={(e) => setVenueForm({ ...venueForm, investment_remarks: e.target.value })}
                    placeholder="e.g., flooring, lighting, equipment upgrades"
                    data-testid="venue-investment-remarks-input"
                  />
                  <p className="text-xs text-slate-400">Construction, flooring, lighting, equipment, upgrades</p>
                </div>
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between pt-2 border-t">
              <Label htmlFor="venue_active">Active</Label>
              <Switch
                id="venue_active"
                checked={venueForm.is_active}
                onCheckedChange={(checked) => setVenueForm({ ...venueForm, is_active: checked })}
                data-testid="venue-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setVenueModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveVenue} data-testid="save-venue-btn">
              {editingVenue ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Court Modal ─────────────────────────────────────────────────────── */}
      <Dialog open={courtModalOpen} onOpenChange={setCourtModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingCourt ? 'Edit Court' : 'Add New Court'}
              {selectedVenueForCourt && (
                <span className="block text-sm font-normal text-slate-500 mt-1">for {selectedVenueForCourt.name}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="court_name">Court Name *</Label>
              <Input
                id="court_name"
                value={courtForm.name}
                onChange={(e) => setCourtForm({ ...courtForm, name: e.target.value })}
                placeholder="e.g., Court-1"
                data-testid="court-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Games Supported *</Label>
              <div className="flex flex-wrap gap-2">
                {ALL_AVAILABLE_GAMES.map((game) => (
                  <Button
                    key={game} type="button" size="sm"
                    variant={courtForm.games.includes(game) ? 'default' : 'outline'}
                    onClick={() => toggleGameForCourt(game)}
                    data-testid={`game-toggle-${game}`}
                  >
                    {game}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="court_price">Default Price per Slot (₹)</Label>
              <Input
                id="court_price"
                type="number" min="0"
                value={courtForm.price_per_slot}
                onChange={(e) => setCourtForm({ ...courtForm, price_per_slot: parseFloat(e.target.value) || 0 })}
                placeholder="e.g., 250"
                data-testid="court-price-input"
              />
              <p className="text-xs text-slate-500">Fallback price if no pricing rules apply</p>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="court_active">Active</Label>
              <Switch
                id="court_active"
                checked={courtForm.is_active}
                onCheckedChange={(checked) => setCourtForm({ ...courtForm, is_active: checked })}
                data-testid="court-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCourtModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCourt} data-testid="save-court-btn">
              {editingCourt ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ──────────────────────────────────────────────── */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deletingItem?.type === 'venue' ? 'Venue' : 'Court'}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingItem?.name}"?
              {deletingItem?.type === 'venue' && ' You must delete all courts first.'}
              {' '}This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

// ── Small presentational sub-component ───────────────────────────────────────
function FacilityMetric({ icon, label, value, highlight }) {
  return (
    <div className={`rounded-lg p-3 ${highlight ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-slate-200'}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <p className="text-xs text-slate-500 font-medium">{label}</p>
      </div>
      <p className={`text-sm font-semibold ${highlight ? 'text-amber-700' : 'text-slate-800'} ${!value ? 'text-slate-400 font-normal' : ''}`}>
        {value || '—'}
      </p>
    </div>
  );
}
